import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, X, AlertCircle, RefreshCw, Edit2, Trash2,
  MessageCircle, Mail, MessageSquare, Radio, Camera, Globe, Target, Sun, Moon,
} from 'lucide-react';

import { useTheme, useFeatureFlags } from '../components/Navbar';
import Toast from '../components/Toast';
import useAutoLogout from '../hooks/useAutoLogout';
import API_BASE from '../config';

const TK = {
  light: {
    pageBg:'#F5F6FA', card:'#FFFFFF', border:'#ECEEF2',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    input:'#FFFFFF', chip:'#F3F4F6', chipTxt:'#374151',
    btnPri:'#5B4EE8', btnPriTxt:'#fff',
    btnSec:'#FFFFFF', btnSecTxt:'#374151',
    overlay:'rgba(15,22,35,0.55)', toggleBg:'#E5E7EB',
    errBg:'#FEF2F2', errBdr:'#FECACA', errTxt:'#DC2626',
    dltBg:'#F8F9FF', shadow:'0 2px 8px rgba(91,78,232,0.08)',
  },
  dark: {
    pageBg:'#0D0D14', card:'#131320', border:'#1E1E2E',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    input:'#18182A', chip:'#1A1A2A', chipTxt:'#C7CBDA',
    btnPri:'#6C63FF', btnPriTxt:'#fff',
    btnSec:'#1A1A2A', btnSecTxt:'#D1D5DB',
    overlay:'rgba(0,0,0,0.72)', toggleBg:'#2A2A3A',
    errBg:'rgba(220,38,38,0.12)', errBdr:'rgba(220,38,38,0.3)', errTxt:'#F87171',
    dltBg:'#0f0f1e', shadow:'0 2px 8px rgba(0,0,0,0.4)',
  },
};

const STATUS_PALETTE = {
  active:   { dot:'#22C55E', bg:'rgba(34,197,94,0.14)',   txt:'#16A34A', label:'Active'      },
  soon:     { dot:'#F59E0B', bg:'rgba(245,158,11,0.14)',  txt:'#D97706', label:'Coming Soon' },
  inactive: { dot:'#9CA3AF', bg:'rgba(156,163,175,0.16)', txt:'#6B7280', label:'Not Active'  },
};

const CHANNELS = [
  { id:'whatsapp',  label:'WhatsApp',     Icon:MessageCircle, color:'#22C55E', kind:'message' },
  { id:'email',     label:'Email',        Icon:Mail,          color:'#3B82F6', kind:'message' },
  { id:'sms',       label:'SMS',          Icon:MessageSquare, color:'#F59E0B', kind:'message' },
  { id:'rcs',       label:'RCS',          Icon:Radio,         color:'#06B6D4', kind:'message' },
  { id:'instagram', label:'Instagram',    Icon:Camera,        color:'#E1306C', kind:'message' },
  { id:'facebook',  label:'Facebook Ads', Icon:Globe,         color:'#1877F2', kind:'ads'     },
  { id:'google',    label:'Google Ads',   Icon:Target,        color:'#EA4335', kind:'ads'     },
];
const CHANNEL_BY_ID = CHANNELS.reduce((m,c) => { m[c.id]=c; return m; }, {});

const CHANNEL_FLAG_MAP = { whatsapp:'whatsapp_campaigns', email:'email_journeys', sms:'sms_channel', rcs:'rcs_channel', instagram:'instagram_channel', facebook:'facebook_sync', google:'google_sync' };
const UNBUILT_CHANNELS = ['rcs', 'instagram'];

const CREATABLE = [
  { id:'whatsapp', label:'WhatsApp'   }, { id:'email',    label:'Email'        },
  { id:'sms',      label:'SMS'        }, { id:'rcs',      label:'RCS'         },
  { id:'instagram',label:'Instagram'  }, { id:'facebook', label:'Facebook Ads'},
  { id:'google',   label:'Google Ads' },
];

const CATEGORIES = ['General','Onboarding','Newsletter','Retention','Transactional','Promotional','Reminder'];
const LANGUAGES  = ['en','hi','ta','te','mr','bn','gu'];

const getToken = () =>
  localStorage.getItem('martech_token') || localStorage.getItem('token') ||
  sessionStorage.getItem('martech_token') || sessionStorage.getItem('token') || '';

const keyFor = (channel, tpl) =>
  `${channel}::${(tpl.name||tpl.id||'tpl').toString().trim().toLowerCase().replace(/\s+/g,'_')}`;

// ---- Mobile responsive hook (JS-driven, per project pattern) ----
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function StatusPill({ status }) {
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.inactive;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:p.bg, color:p.txt, padding:'3px 10px', borderRadius:9999, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:p.dot, flexShrink:0 }} />{p.label}
    </span>
  );
}

function ChannelChip({ ch, status, active, onClick, tk, isMobile }) {
  const { Icon, label, color } = ch;
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.inactive;
  return (
    <button onClick={onClick}
      style={{
        display:'flex', alignItems:'center', gap:8,
        padding: isMobile ? '8px 10px' : '8px 12px',
        borderRadius:14, border:'1px solid', cursor:'pointer',
        flexShrink:0, background: active ? tk.accentBg : tk.card,
        borderColor: active ? tk.accent : tk.border,
        minWidth: isMobile ? 'auto' : 0,
      }}>
      <span style={{ width:26, height:26, borderRadius:8, background:`${color}1f`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon style={{ width:14, height:14, color }} />
      </span>
      <span style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', lineHeight:1.2 }}>
        <span style={{ fontSize:12, fontWeight:700, color: active ? tk.accentTxt : tk.text, whiteSpace:'nowrap' }}>{label}</span>
        {!isMobile && (
          <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:600, color:p.txt }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:p.dot }} />{p.label}
          </span>
        )}
      </span>
    </button>
  );
}

function Toggle({ on, onClick, color, tk }) {
  return (
    <button onClick={onClick} aria-pressed={on}
      style={{ width:38, height:22, borderRadius:9999, border:'none', cursor:'pointer', background: on ? color : tk.toggleBg, position:'relative', transition:'background .15s', flexShrink:0, padding:0 }}>
      <span style={{ position:'absolute', top:2, left: on ? 18 : 2, width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left .15s', boxShadow:'0 1px 2px rgba(0,0,0,0.3)' }} />
    </button>
  );
}

function StatusBadge({ status, tk }) {
  const map = {
    approved: { bg:'rgba(16,185,129,0.14)', color:'#10b981', label:'Approved' },
    active:   { bg:tk.accentBg,             color:tk.accentTxt, label:'Active' },
    pending:  { bg:'rgba(245,158,11,0.14)', color:'#f59e0b',  label:'Pending'  },
    rejected: { bg:'rgba(239,68,68,0.14)',  color:'#ef4444',  label:'Rejected' },
    draft:    { bg:'rgba(245,158,11,0.14)', color:'#f59e0b',  label:'Draft'    },
  };
  const s = map[(status||'').toLowerCase()] || { bg:tk.chip, color:tk.muted, label:status||'—' };
  return <span style={{ background:s.bg, color:s.color, padding:'2px 8px', borderRadius:9999, fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>{s.label}</span>;
}

function CategoryBadge({ category, tk }) {
  return <span style={{ background:tk.accentBg, color:tk.accentTxt, padding:'2px 8px', borderRadius:9999, fontSize:10, fontWeight:700, textTransform:'capitalize', whiteSpace:'nowrap' }}>{category||'General'}</span>;
}

function TemplateCard({ tpl, ch, tk, enabled, editable, onToggle, onEdit, onDelete, isMobile }) {
  const { Icon, color } = ch;
  const content = tpl.content || tpl.body || tpl.preview || '';
  const preview = content.slice(0,120) + (content.length > 120 ? '…' : '');
  return (
    <div style={{ background:tk.card, border:`1px solid ${tk.border}`, borderRadius:16, opacity:enabled?1:0.55, transition:'opacity .15s', boxShadow:tk.shadow, padding: isMobile ? 12 : 14, display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', flexDirection:'row', alignItems:'flex-start', gap:10, marginBottom:10 }}>
        <div style={{ width:34, height:34, borderRadius:10, background:`${color}1f`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Icon style={{ width:17, height:17, color }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ color:tk.text, fontWeight:700, fontSize:13, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{tpl.name}</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:5 }}>
            <CategoryBadge category={tpl.category} tk={tk} />
            <StatusBadge status={tpl.status || tpl.approval_status || 'active'} tk={tk} />
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, flexShrink:0 }}>
          <Toggle on={enabled} onClick={onToggle} color={color} tk={tk} />
          <span style={{ fontSize:9, fontWeight:700, color:enabled?color:tk.muted, textTransform:'uppercase' }}>{enabled?'On':'Off'}</span>
        </div>
      </div>

      {preview && (
        <p style={{ color:tk.sub, fontSize:12, fontStyle:'italic', marginBottom:10, lineHeight:1.5, wordBreak:'break-word', margin:'0 0 10px' }}>"{preview}"</p>
      )}

      {(tpl.sender_id || tpl.dlt_template_id) && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:10 }}>
          {tpl.sender_id && <span style={{ background:tk.chip, color:tk.chipTxt, padding:'2px 8px', borderRadius:9999, fontSize:10, fontWeight:600 }}>Sender: {tpl.sender_id}</span>}
          {tpl.dlt_template_id && <span style={{ background:tk.chip, color:tk.chipTxt, padding:'2px 8px', borderRadius:9999, fontSize:10, fontWeight:600, wordBreak:'break-all' }}>DLT: {tpl.dlt_template_id}</span>}
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingTop:10, borderTop:`1px solid ${tk.border}`, marginTop:'auto', gap:8 }}>
        <span style={{ background:tk.chip, color:tk.chipTxt, padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600, flexShrink:0 }}>
          {(tpl.language||tpl.lang||'en').toUpperCase()}
        </span>
        <div style={{ display:'flex', flexDirection:'row', gap:6 }}>
          <button onClick={() => onEdit && onEdit(tpl)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontSize:12, fontWeight:600, padding: isMobile ? '8px 12px' : '5px 10px', minHeight: isMobile ? 36 : 'auto', borderRadius:8, background:tk.chip, color:editable?tk.sub:tk.muted, border:'none', cursor:'pointer' }}>
            <Edit2 style={{ width:11, height:11 }} /> {!isMobile && 'Edit'}
          </button>
          <button onClick={() => onDelete && onDelete(tpl)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontSize:12, fontWeight:600, padding: isMobile ? '8px 12px' : '5px 10px', minHeight: isMobile ? 36 : 'auto', borderRadius:8, background:tk.chip, color:editable?tk.sub:tk.muted, border:'none', cursor:'pointer' }}>
            <Trash2 style={{ width:11, height:11 }} /> {!isMobile && 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ tk, icon: Icon, color, title, body, isMobile }) {
  return (
    <div style={{ background:tk.card, border:`1px solid ${tk.border}`, borderRadius:20, padding: isMobile ? '32px 18px' : '48px 24px', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
      <div style={{ width:48, height:48, borderRadius:14, background:`${color}1f`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
        <Icon style={{ width:24, height:24, color }} />
      </div>
      <p style={{ color:tk.text, fontSize:15, fontWeight:700, margin:0 }}>{title}</p>
      <p style={{ color:tk.sub, fontSize:13, marginTop:8, maxWidth:320, lineHeight:1.6, margin:'8px auto 0' }}>{body}</p>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6, color:'#9CA3AF' }}>
        {label}{required && <span style={{ color:'#ef4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function ContentManager() {
  const navigate = useNavigate();
  useAutoLogout();
  const isMobile = useIsMobile();

  const ctx = useTheme() || {};
  const [lsTheme, setLsTheme] = useState(() => {
    try { return localStorage.getItem('martech_theme') || 'light'; } catch { return 'light'; }
  });
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const t = localStorage.getItem('martech_theme') || 'light';
        setLsTheme(p => p !== t ? t : p);
      } catch { /* ignore */ }
    }, 400);
    return () => clearInterval(id);
  }, []);
  const theme = (ctx.theme || lsTheme) === 'dark' ? 'dark' : 'light';
  const tk = TK[theme];

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem('martech_theme', next); } catch { /* ignore */ }
    setLsTheme(next);
    if (typeof ctx.setTheme === 'function') ctx.setTheme(next);
  };

  const [waTemplates,    setWaTemplates]    = useState([]);
  const [storeTemplates, setStoreTemplates] = useState([]);
  const [toggles,        setToggles]        = useState({});
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [activeTab,      setActiveTab]      = useState('whatsapp');
  const [toast,          setToast]          = useState(null);

  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [tplName,     setTplName]     = useState('');
  const [tplContent,  setTplContent]  = useState('');
  const [tplChannel,  setTplChannel]  = useState('whatsapp');
  const [tplCategory, setTplCategory] = useState('General');
  const [tplLang,     setTplLang]     = useState('en');
  const [tplPeId,     setTplPeId]     = useState('');
  const [tplDltId,    setTplDltId]    = useState('');
  const [tplSenderId, setTplSenderId] = useState('MRKTECH');
  const [submitting,  setSubmitting]  = useState(false);

  const showToast = (msg, variant = 'green') => setToast({ msg, variant });

  const fetchWaTemplates = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/v1/compliance/templates`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      if (d.success !== false) {
        const rows = Array.isArray(d.templates) ? d.templates : Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : [];
        setWaTemplates(rows);
      } else { setError(d.message || 'Failed to load templates'); }
    } catch { setError('Network error — could not load templates'); }
    setLoading(false);
  }, [navigate]);

  const fetchStoreTemplates = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/templates`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      if (!r.ok) return;
      const d = await r.json();
      const rows = Array.isArray(d.templates) ? d.templates : Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : [];
      setStoreTemplates(rows);
    } catch { /* keep */ }
  }, []);


  const fetchToggles = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/channels/template-status`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      if (!r.ok) return;
      const d = await r.json();
      const rows = Array.isArray(d.statuses) ? d.statuses : Array.isArray(d.data) ? d.data : [];
      const m = {};
      rows.forEach(row => { if (row && row.template_key) m[row.template_key] = row.enabled !== false; });
      setToggles(m);
    } catch { /* keep */ }
  }, []);

  const refreshAll = useCallback(() => {
    fetchWaTemplates(); fetchStoreTemplates(); fetchToggles();
  }, [fetchWaTemplates, fetchStoreTemplates, fetchToggles]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Prevent background scroll leaking behind the modal on mobile
  useEffect(() => {
    if (modalOpen && isMobile) {
      const prevOverflow = document.body.style.overflow;
      const prevPosition = document.body.style.position;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.position = prevPosition;
        document.body.style.width = '';
      };
    }
  }, [modalOpen, isMobile]);

  const whatsappTemplates = waTemplates.filter(t => !t.channel || (t.channel||'').toLowerCase() === 'whatsapp');
  const channelTemplates  = chId => chId === 'whatsapp' ? whatsappTemplates : storeTemplates.filter(t => (t.channel||'').toLowerCase() === chId);
  const { flags } = useFeatureFlags();
  const computedStatus = (id) => UNBUILT_CHANNELS.includes(id) ? 'soon' : (flags[CHANNEL_FLAG_MAP[id]] === false ? 'inactive' : 'active');
  const visibleChannels = CHANNELS.filter((ch) => computedStatus(ch.id) !== 'inactive');
  const displayed         = channelTemplates(activeTab);
  const activeCh          = CHANNEL_BY_ID[activeTab];
  const activeStatus      = computedStatus(activeTab);
  const hasTemplateConcept = activeCh && activeCh.kind === 'message';
  const isEditable        = tpl => activeTab !== 'whatsapp' && typeof tpl.id === 'number';

  const resetForm = (defaultChannel = 'whatsapp') => {
    setEditingId(null); setTplName(''); setTplContent(''); setTplChannel(defaultChannel);
    setTplCategory('General'); setTplLang('en'); setTplPeId(''); setTplDltId(''); setTplSenderId('MRKTECH');
  };
  const openCreate = () => {
    const isCreatableTab = CREATABLE.some(c => c.id === activeTab);
    resetForm(isCreatableTab ? activeTab : 'whatsapp');
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const handleEdit = tpl => {
    if (activeTab === 'whatsapp') { showToast('WhatsApp templates are managed in Meta Business Manager', 'blue'); return; }
    if (!isEditable(tpl)) { showToast('This template is not editable', 'blue'); return; }
    setEditingId(tpl.id); setTplName(tpl.name||''); setTplContent(tpl.content||tpl.body||'');
    setTplChannel((tpl.channel||activeTab).toLowerCase()); setTplCategory(tpl.category||'General');
    setTplLang(tpl.language||tpl.lang||'en'); setTplPeId(tpl.pe_id||'');
    setTplDltId(tpl.dlt_template_id||''); setTplSenderId(tpl.sender_id||'MRKTECH');
    setModalOpen(true);
  };

  const handleDelete = async tpl => {
    if (activeTab === 'whatsapp') { showToast('WhatsApp templates are managed in Meta Business Manager', 'blue'); return; }
    if (!isEditable(tpl)) { showToast('This template is not deletable', 'blue'); return; }
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      const r = await fetch(`${API_BASE}/api/v1/templates/${tpl.id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${getToken()}` } });
      if (!r.ok) throw new Error('failed');
      showToast('Template deleted'); fetchStoreTemplates();
    } catch { showToast('Could not delete template', 'red'); }
  };

  const smsIncomplete = tplChannel === 'sms' && (!tplPeId.trim() || !tplDltId.trim() || !tplSenderId.trim());

  const handleSave = async e => {
    e.preventDefault();
    if (!tplName.trim() || !tplContent.trim()) return;
    if (smsIncomplete) { showToast('PE ID, DLT Template ID and Sender ID are required for SMS', 'red'); return; }
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    setSubmitting(true);
    try {
      if (tplChannel === 'whatsapp' && !editingId) {
        const r = await fetch(`${API_BASE}/api/v1/compliance/templates`, {
          method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
          body: JSON.stringify({ name:tplName.trim(), content:tplContent.trim(), channel:'whatsapp', category:tplCategory, language:tplLang }),
        });
        const d = await r.json();
        if (r.ok && d.success !== false) { showToast('Template submitted'); closeModal(); fetchWaTemplates(); }
        else showToast(d.message || 'Failed to create template', 'red');
      } else {
        const body = {
          name:tplName.trim(), content:tplContent.trim(), channel:tplChannel,
          category:tplCategory, language:tplLang,
          pe_id: tplChannel === 'sms' ? tplPeId.trim() : null,
          dlt_template_id: tplChannel === 'sms' ? tplDltId.trim() : null,
          sender_id: tplChannel === 'sms' ? tplSenderId.trim() : null,
        };
        const url    = editingId ? `${API_BASE}/api/v1/templates/${editingId}` : `${API_BASE}/api/v1/templates`;
        const method = editingId ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' }, body:JSON.stringify(body) });
        const d = await r.json();
        if (r.ok && d.success !== false) { showToast(editingId ? 'Template updated' : 'Template created'); closeModal(); fetchStoreTemplates(); }
        else showToast(d.message || 'Failed to save template', 'red');
      }
    } catch { showToast('Network error', 'red'); }
    setSubmitting(false);
  };

  const onToggleTemplate = async (channel, tpl) => {
    const key = keyFor(channel, tpl);
    const cur = toggles[key] !== false;
    const next = !cur;
    setToggles(p => ({ ...p, [key]: next }));
    try {
      const r = await fetch(`${API_BASE}/api/v1/channels/template-status`, {
        method:'PUT', headers:{ Authorization:`Bearer ${getToken()}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ template_key:key, channel, enabled:next }),
      });
      if (!r.ok) throw new Error('failed');
      showToast(next ? 'Template enabled' : 'Template disabled', next ? 'green' : 'blue');
    } catch {
      setToggles(p => ({ ...p, [key]: cur }));
      showToast('Could not save toggle', 'red');
    }
  };

  const IS  = { background:tk.input, border:`1px solid ${tk.border}`, color:tk.text, borderRadius:12, padding: isMobile ? '10px 12px' : '10px 14px', width:'100%', fontSize: isMobile ? 16 : 14, outline:'none', boxSizing:'border-box' };
  const IS2 = { ...IS, background:tk.card };

  return (
    <div style={{ background:tk.pageBg, minHeight:'100vh', width:'100%', maxWidth:'100vw', overflowX:'hidden', boxSizing:'border-box' }}>
      {/* mobile navbar spacer */}
      {isMobile && <div style={{ height:56 }} />}

      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main style={{
        maxWidth: 1200, margin:'0 auto',
        padding: isMobile ? '16px 14px 32px' : '28px 32px 48px',
        boxSizing:'border-box',
      }}>

        {/* header */}
        <div style={{
          display:'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent:'space-between',
          marginBottom:24, gap:12,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:tk.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <FileText style={{ width:20, height:20, color:tk.accent }} />
            </div>
            <div style={{ minWidth:0 }}>
              <h1 style={{ fontSize: isMobile ? 18 : 20, fontWeight:800, color:tk.text, letterSpacing:'-0.4px', margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>Content Manager</h1>
              <p style={{ color:tk.sub, fontSize:12, margin:'2px 0 0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>Templates across all 7 channels</p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
            <button onClick={toggleTheme} title="Toggle theme"
              style={{ width: isMobile ? 40 : 36, height: isMobile ? 40 : 36, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', background:tk.card, border:`1px solid ${tk.border}`, color:tk.sub, cursor:'pointer', flexShrink:0 }}>
              {theme === 'dark' ? <Sun style={{ width:16, height:16 }} /> : <Moon style={{ width:16, height:16 }} />}
            </button>
            <button onClick={refreshAll} title="Refresh"
              style={{ width: isMobile ? 40 : 36, height: isMobile ? 40 : 36, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', background:tk.card, border:`1px solid ${tk.border}`, color:tk.sub, cursor:'pointer', flexShrink:0 }}>
              <RefreshCw style={{ width:15, height:15 }} />
            </button>
            <button onClick={openCreate}
              style={{ display:'flex', alignItems:'center', gap:5, padding: isMobile ? '0 14px' : '0 10px', height: isMobile ? 40 : 36, borderRadius:10, fontSize:13, fontWeight:700, background:tk.btnPri, color:tk.btnPriTxt, border:'none', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
              <Plus style={{ width:15, height:15 }} />
              Create
            </button>
          </div>
        </div>

        {/* error */}
        {error && (
          <div style={{
            display:'flex', alignItems:'center', gap:10,
            background:tk.errBg, border:`1px solid ${tk.errBdr}`, color:tk.errTxt,
            borderRadius:12, padding:'10px 14px', marginBottom:20, fontSize:13,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}>
            <AlertCircle style={{ width:16, height:16, flexShrink:0 }} />
            <span style={{ flex:1, minWidth: isMobile ? '100%' : 0 }}>{error}</span>
            <button onClick={fetchWaTemplates}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:8, fontSize:12, fontWeight:700, color:tk.errTxt, background:'transparent', border:'none', cursor:'pointer' }}>
              <RefreshCw style={{ width:12, height:12 }} /> Retry
            </button>
          </div>
        )}

        {/* channel chips */}
        <div style={{ marginBottom:20 }}>
          <p style={{ color:tk.muted, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>Channels</p>
          <div style={{
            display:'flex', gap:8,
            overflowX: isMobile ? 'auto' : 'visible',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            paddingBottom: isMobile ? 6 : 0,
            marginBottom: isMobile ? -6 : 0,
            WebkitOverflowScrolling:'touch',
            scrollSnapType: isMobile ? 'x proximity' : 'none',
            scrollbarWidth:'none',
            msOverflowStyle:'none',
          }} className="cm-chip-scroll">
            {visibleChannels.map(ch => (
              <div key={ch.id} style={{ scrollSnapAlign: isMobile ? 'start' : 'unset', flexShrink:0 }}>
                <ChannelChip ch={ch} status={computedStatus(ch.id)} active={activeTab===ch.id} onClick={() => setActiveTab(ch.id)} tk={tk} isMobile={isMobile} />
              </div>
            ))}
          </div>
        </div>

        {/* active channel banner */}
        {activeCh && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            background:tk.card, border:`1px solid ${tk.border}`, boxShadow:tk.shadow,
            borderRadius:16, padding: isMobile ? '12px 14px' : '16px 20px', marginBottom:20, gap:10,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
              <div style={{ width:40, height:40, borderRadius:11, background:`${activeCh.color}1f`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <activeCh.Icon style={{ width:20, height:20, color:activeCh.color }} />
              </div>
              <div style={{ minWidth:0 }}>
                <p style={{ color:tk.text, fontSize:15, fontWeight:800, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{activeCh.label}</p>
                <p style={{ color:tk.sub, fontSize:12, margin:'2px 0 0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {activeStatus==='active' ? 'Connected & sending' : activeStatus==='soon' ? 'Launching Week 28' : 'Not configured yet'}
                </p>
              </div>
            </div>
            <div style={{ flexShrink:0 }}><StatusPill status={activeStatus} /></div>
          </div>
        )}

        {/* template grid */}
        {loading && activeTab === 'whatsapp' ? (
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap:14 }}>
            {Array(4).fill(null).map((_,i) => (
              <div key={i} style={{ background:tk.card, border:`1px solid ${tk.border}`, borderRadius:20, padding:20 }}>
                <div style={{ display:'flex', gap:12, marginBottom:12 }}>
                  <div style={{ width:36, height:36, borderRadius:10, flexShrink:0, background:tk.chip, animation:'cmPulse 1.4s ease-in-out infinite' }} />
                  <div style={{ flex:1 }}>
                    <div style={{ height:16, width:'60%', marginBottom:8, borderRadius:6, background:tk.chip, animation:'cmPulse 1.4s ease-in-out infinite' }} />
                    <div style={{ height:12, width:'40%', borderRadius:6, background:tk.chip, animation:'cmPulse 1.4s ease-in-out infinite' }} />
                  </div>
                </div>
                <div style={{ height:12, width:'100%', marginBottom:6, borderRadius:6, background:tk.chip, animation:'cmPulse 1.4s ease-in-out infinite' }} />
                <div style={{ height:12, width:'75%', borderRadius:6, background:tk.chip, animation:'cmPulse 1.4s ease-in-out infinite' }} />
              </div>
            ))}
          </div>
        ) : !hasTemplateConcept ? (
          <InfoCard tk={tk} icon={activeCh.Icon} color={activeCh.color} isMobile={isMobile}
            title={`${activeCh.label} — audience sync`}
            body={activeStatus==='active'
              ? `${activeCh.label} syncs CDP segments as custom audiences. No message templates needed.`
              : `${activeCh.label} not connected yet. Once configured it syncs audiences — no templates needed.`} />
        ) : activeStatus === 'soon' && displayed.length === 0 ? (
          <InfoCard tk={tk} icon={activeCh.Icon} color={activeCh.color} isMobile={isMobile}
            title={`${activeCh.label} launches in Week 28`}
            body={`Pre-author templates now — they will be ready to send the moment ${activeCh.label} goes live.`} />
        ) : displayed.length === 0 ? (
          <InfoCard tk={tk} icon={FileText} color={tk.muted} isMobile={isMobile}
            title={`No ${activeCh.label} templates yet`} body='Click "Create Template" to add your first one.' />
        ) : (
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap:14 }}>
            {displayed.map((tpl,idx) => {
              const key = keyFor(activeTab, tpl);
              const enabled = toggles[key] !== false;
              return (
                <TemplateCard key={tpl.id ?? tpl._id ?? idx} tpl={tpl} ch={activeCh} tk={tk} isMobile={isMobile}
                  enabled={enabled} editable={isEditable(tpl)}
                  onToggle={() => onToggleTemplate(activeTab, tpl)}
                  onEdit={handleEdit} onDelete={handleDelete} />
              );
            })}
          </div>
        )}
      </main>

      {/* modal */}
      {modalOpen && (
        <div style={{
          position:'fixed', inset:0, zIndex:1000,
          background:tk.overlay, backdropFilter:'blur(6px)',
          display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center',
          padding: isMobile ? 0 : 20,
        }}>
          <div style={{
            background:tk.card, border:`1px solid ${tk.border}`,
            width:'100%',
            maxWidth: isMobile ? '100%' : 480,
            maxHeight: isMobile ? '92vh' : '88vh',
            overflowY:'auto',
            overscrollBehavior:'contain',
            WebkitOverflowScrolling:'touch',
            borderRadius: isMobile ? '20px 20px 0 0' : 20,
            padding: isMobile ? '10px 16px calc(24px + env(safe-area-inset-bottom, 0px))' : 28,
            boxSizing:'border-box',
          }}>
            {isMobile && (
              <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
                <div style={{ width:36, height:4, borderRadius:9999, background:tk.border }} />
              </div>
            )}

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <div style={{ width:32, height:32, borderRadius:10, background:tk.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FileText style={{ width:16, height:16, color:tk.accent }} />
                </div>
                <h2 style={{ fontSize:15, fontWeight:800, color:tk.text, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{editingId ? 'Edit Template' : 'Create Template'}</h2>
              </div>
              <button onClick={closeModal} style={{ background:'none', border:'none', cursor:'pointer', color:tk.sub, padding:0, width: isMobile ? 40 : 28, height: isMobile ? 40 : 28, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <X style={{ width:18, height:18 }} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <Field label="Name" required>
                <input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="e.g. Order Confirmation" required style={IS}
                  onFocus={e => { e.target.style.borderColor=tk.accent; }} onBlur={e => { e.target.style.borderColor=tk.border; }} />
              </Field>

              <Field label="Channel">
                <select value={tplChannel} onChange={e => setTplChannel(e.target.value)} style={IS}>
                  {CREATABLE.filter(c => computedStatus(c.id) !== 'inactive').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>

              {tplChannel === 'sms' && (
                <div style={{ background:tk.dltBg, border:`1px solid ${tk.border}`, borderRadius:14, padding: isMobile ? 12 : 16 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:tk.accent, margin:'0 0 12px', textTransform:'uppercase', letterSpacing:'0.05em' }}>DLT Compliance — required for SMS</p>
                  <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12 }}>
                    <Field label="PE ID" required>
                      <input value={tplPeId} onChange={e => setTplPeId(e.target.value)} placeholder="Principal Entity ID" required style={IS2}
                        onFocus={e => { e.target.style.borderColor=tk.accent; }} onBlur={e => { e.target.style.borderColor=tk.border; }} />
                    </Field>
                    <Field label="Sender ID" required>
                      <input value={tplSenderId} onChange={e => setTplSenderId(e.target.value)} placeholder="MRKTECH" required style={IS2}
                        onFocus={e => { e.target.style.borderColor=tk.accent; }} onBlur={e => { e.target.style.borderColor=tk.border; }} />
                    </Field>
                  </div>
                  <div style={{ marginTop:12 }}>
                    <Field label="DLT Template ID" required>
                      <input value={tplDltId} onChange={e => setTplDltId(e.target.value)} placeholder="Registered DLT template ID" required style={IS2}
                        onFocus={e => { e.target.style.borderColor=tk.accent; }} onBlur={e => { e.target.style.borderColor=tk.border; }} />
                    </Field>
                  </div>
                </div>
              )}

              <Field label="Category">
                <select value={tplCategory} onChange={e => setTplCategory(e.target.value)} style={IS}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <Field label={CHANNEL_BY_ID[tplChannel]?.kind === 'ads' ? 'Ad Copy / Audience Description' : 'Content'} required>
                <textarea value={tplContent} onChange={e => setTplContent(e.target.value)} placeholder={CHANNEL_BY_ID[tplChannel]?.kind === 'ads' ? 'e.g. Retarget visitors who viewed pricing in last 30 days' : 'Hello {{name}}, your order is confirmed!'} required rows={4}
                  style={{ ...IS, resize:'vertical', lineHeight:1.6 }}
                  onFocus={e => { e.target.style.borderColor=tk.accent; }} onBlur={e => { e.target.style.borderColor=tk.border; }} />
              </Field>

              <Field label="Language">
                <select value={tplLang} onChange={e => setTplLang(e.target.value)} style={IS}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                </select>
              </Field>

              <div style={{ display:'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap:10, marginTop:4 }}>
                <button type="button" onClick={closeModal}
                  style={{ flex:1, padding:'11px 16px', borderRadius:12, fontSize:14, fontWeight:700, background:tk.btnSec, border:`1px solid ${tk.border}`, color:tk.btnSecTxt, cursor:'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting || !tplName.trim() || !tplContent.trim() || smsIncomplete}
                  style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'11px 16px', borderRadius:12, fontSize:14, fontWeight:700, background:tk.btnPri, color:tk.btnPriTxt, border:'none', cursor:'pointer', opacity:(submitting||!tplName.trim()||!tplContent.trim()||smsIncomplete)?0.5:1 }}>
                  {submitting ? <RefreshCw style={{ width:16, height:16, animation:'cmSpin 1s linear infinite' }} /> : <Plus style={{ width:16, height:16 }} />}
                  {submitting ? 'Saving…' : editingId ? 'Save Changes' : 'Save Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* keyframes + minor cross-browser fixes only — cannot be expressed as inline style */}
      <style>{`
        @keyframes cmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes cmPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        .cm-chip-scroll::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}